<template>
  <div class="contacts-container">

    <!-- Encabezado: título principal y filtros -->
    <section class="contacts-header">
      <!-- FILA 1: TÍTULO + SELECTORES -->
      <div class="contacts-header__top">
        <div class="contacts-header__text">
          <h1 class="contacts-header__title">Clientes</h1>
          <p class="contacts-header__subtitle">Visualiza todos tus contactos</p>
        </div>

        <div class="contacts-filters">
          <!-- Selector de línea -->
          <div class="filter-group">
            <ion-icon name="git-branch-outline" class="select-icon"></ion-icon>
            <select v-model.number="selectedLineId" :disabled="loading">
              <option v-for="line in lines" :key="line.id" :value="line.id">
                {{ line.line_name }}
              </option>
            </select>
          </div>

          <!-- Escalado -->
          <div class="filter-group">
            <ion-icon name="warning-outline" class="select-icon"></ion-icon>
            <select v-model="escalatedFilter" :disabled="loading">
              <option value="">Todos</option>
              <option value="1">Escalado</option>
              <option value="0">No escalado</option>
            </select>
          </div>

          <!-- Tags -->
          <div class="filter-group">
            <ion-icon name="pricetags-outline" class="select-icon"></ion-icon>
            <select v-model="selectedTags" :disabled="loading">
              <option value="">Todos</option>
              <option v-for="t in availableTags" :key="t" :value="t">{{ t }}</option>
            </select>
          </div>
          
          <!-- Solo con registro de agenda -->
          <button
            class="chip-toggle"
            :class="{ active: scheduledOnly }"
            @click="scheduledOnly = !scheduledOnly"
            :disabled="loading"
            title="Solo con registro de agenda"
          >
            <ion-icon name="calendar-outline" class="chip-icon"></ion-icon>
            <span>Agendados</span>
          </button>

        </div>
      </div>

      <!-- FILA 2: BÚSQUEDA (IZQUIERDA) -->
      <div class="contacts-search">
        <div class="filter-group filter-input">
          <ion-icon name="search-outline" class="select-icon"></ion-icon>
          <input
            type="text"
            v-model.trim="searchQuery"
            placeholder="Buscar nombre o teléfono..."
            :disabled="loading"
          />
        </div>
      </div>
    </section>

    <div class="contacts-table">
      <ContactsTable
        :contacts="contacts"
        :loading="loading"
        @chat="onChat"
        @delete="onDelete"
        @manage-tags="openTagModal"
        @toggle-record="toggleRecord"
      />
    </div>

    <DeleteContactModal
      v-if="showDeleteModal"
      :contact="contactToDelete"
      :loading="deleting"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
      @close="cancelDelete"
    />

    <TagAssignRemoveModal
      v-if="showTagModal && selectedContact"
      :show="showTagModal"
      :contact-id="selectedContact.id"
      :contact-name="selectedContact.name || selectedContact.phone_number"
      :contact-tags="selectedContact.tags || []"
      @close="showTagModal = false"
      @changed="onTagsChanged"
    />

    <!-- Controles de paginación -->
    <div class="contacts-pagination">
      <button class="pg-btn"
              :disabled="page <= 1 || loading"
              @click="goToPage(page - 1)">
        ‹ Anterior
      </button>

      <span class="pg-info">
        Página <strong>{{ page }}</strong> de <strong>{{ lastPage }}</strong>
        <small v-if="total"> · {{ total }} registros</small>
      </span>

      <button class="pg-btn"
              :disabled="page >= lastPage || loading"
              @click="goToPage(page + 1)">
        Siguiente ›
      </button>
    </div>

  </div>
</template>

<script>
import ContactsTable from '@/components/contacts/ContactsTable.vue' 
import DeleteContactModal from '@/components/contacts/DeleteContactModal.vue' 
import TagAssignRemoveModal from '@/components/contacts/TagAssignRemoveModal.vue';

export default {
  name: 'ContactsView',
  components: { ContactsTable, DeleteContactModal, TagAssignRemoveModal },

  data() {
    return {
      lines: [],           
      selectedLineId: '', 
      loading: false,      
      searchQuery: '',        
      escalatedFilter: '',   
      selectedTags: '', 
      availableTags: [],
      contacts: [],

      // Eliminar
      showDeleteModal: false,   
      contactToDelete: null,      
      deleting: false,  

      // Paginacion
      page: 1,
      perPage: 25,
      total: 0,
      lastPage: 1,

      // debounce para búsqueda
      searchTimer: null,

      // Agenda
      scheduledOnly: false,

      // Tag Modal
      showTagModal: false,
      selectedContact: null,
    };
  },

  async mounted() {
    await this.fetchLines();
    await this.fetchTags();
    if (!this.selectedLineId) {
      await this.fetchContacts();
    }
  },

  watch: {
    selectedLineId() {
      this.applyFiltersAndReload();
    },
    escalatedFilter() {
      this.applyFiltersAndReload();
    },
    selectedTags() {
      this.applyFiltersAndReload();
    },
    searchQuery() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.applyFiltersAndReload();
      }, 350);
    },
    scheduledOnly() {
      this.applyFiltersAndReload();
    },
  },

  methods: {
    async fetchLines() {
      this.loading = true;
      try {
        const { data } = await this.$axios.get("/lines");
        const list = Array.isArray(data) ? data : (data?.data ?? []);
        this.lines = list;

        if (this.lines.length > 0) {
          this.selectedLineId = Number(this.lines[0].id);
        }
      } catch (error) {
        console.error("Error al obtener las líneas desde el backend:", error);
        this.lines = [];
      } finally {
        this.loading = false;
      }
    },

    async fetchTags() {
      try {
        const { data } = await this.$axios.get('/tags');
        const list = Array.isArray(data) ? data : (data?.data ?? []);
        this.availableTags = list.map(t => t.name);
      } catch (err) {
        console.error('Error al obtener tags desde el backend:', err);
        this.availableTags = [];
      }
    },

    buildContactParams() {
      const params = {
        page: this.page,
        per_page: this.perPage,
        line_id: this.selectedLineId || undefined,
      };

      if (this.searchQuery && this.searchQuery.trim() !== '') {
        params.search = this.searchQuery.trim();
      }

      if (this.escalatedFilter !== '') {
        params.escalated = this.escalatedFilter; 
      }

      if (this.selectedTags && this.selectedTags !== '') {
        params.tags = [this.selectedTags];
      }

      if (this.scheduledOnly) {
        params.scheduled_only = 1;
      }

      return params;
    },

    async fetchContacts() {
      try {
        this.loading = true;
        const params = this.buildContactParams();
        const { data } = await this.$axios.get('/contacts/table', { params });

        const rows = Array.isArray(data?.data) ? data.data : [];
        this.contacts = rows;

        this.page     = Number(data?.current_page ?? this.page);
        this.perPage  = Number(data?.per_page ?? this.perPage);
        this.total    = Number(data?.total ?? this.total);
        this.lastPage = Number(data?.last_page ?? this.lastPage);
      } catch (err) {
        console.error('Error al obtener contactos:', err);
        this.contacts = [];
      } finally {
        this.loading = false;
      }
    },

    async applyFiltersAndReload() {
      this.page = 1;
      await this.fetchContacts();
    },

    async goToPage(p) {
      if (p < 1 || p > this.lastPage) return;
      this.page = p;
      await this.fetchContacts();
    },

    setLineId(val) {
      this.selectedLineId = val === '' ? '' : Number(val);
    },

    // === Tabla
    onChat(contact) {
      const lineId = contact.line_id || this.selectedLineId;
      if (!lineId) return;

      this.$router.push({
        name: 'ChatPage',
        params: { lineId },
        query: { phone: contact.phone_number }
      });
    },
    async toggleRecord(contact) {
      try {
        this.$set(contact, '__toggling', true); 

        const { data } = await this.$axios.post(`/contacts/${contact.id}/appointment-toggle`);
        this.$set(contact, 'has_schedule_record', !!data?.exists);

      } catch (e) {
        console.error('Error al alternar registro:', e);
      } finally {
        this.$set(contact, '__toggling', false);
      }
    },
    openTagModal(contact) {
      this.selectedContact = contact;
      this.$nextTick(() => { 
        this.showTagModal = true;
      });
    },
    onTagsChanged({ contactId, tags }) {
      const idx = this.contacts.findIndex(c => c.id === contactId);
      if (idx !== -1) this.$set(this.contacts[idx], 'tags', tags);
    },
    onDelete(contact) {                 
      this.contactToDelete = contact;  
      this.showDeleteModal = true;     
    },
    async confirmDelete(contact) {            
      if (!contact?.id) return;

      this.deleting = true;
      try {
        await this.$axios.delete(`/contacts/${contact.id}`);

        const newTotal = Math.max(0, this.total - 1);
        const newLastPage = Math.max(1, Math.ceil(newTotal / this.perPage));
        if (this.page > newLastPage) {
          this.page = newLastPage;
        }

        await this.fetchContacts();
      } catch (e) {
        console.error('Error eliminando contacto:', e);
      } finally {
        this.deleting = false;
        this.showDeleteModal = false;
        this.contactToDelete = null;
      }
    },
    cancelDelete() {                   
      this.showDeleteModal = false;
      this.contactToDelete = null;
    },
  }
}
</script>

<style scoped>

/* ========== CONTENEDOR PRINCIPAL ========== */
.contacts-container {
  padding: 20px;
  background: #f9fafb;
  height: 100vh;
  overflow-y: auto; 
}

/* ========== ENCABEZADO ========== */
.contacts-header {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;     
  font-family: 'Manrope', sans-serif;
  margin-bottom: 1rem;
}

.contacts-header__top {
  display: flex;
  align-items: flex-end;      
  justify-content: space-between;
  gap: 1rem;
}

.contacts-filters {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.contacts-header__text {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.contacts-header__title {
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.contacts-header__subtitle {
  font-size: 1rem;
  font-weight: 500;
  color: #64748b;
  margin: 0.15rem 0 0 0;
}

/* ========== FILTROS ========== */
.contacts-filters {
  display: flex;
  align-items: center;
  gap: .75rem;
  flex-wrap: wrap;
}

/* === Búsqueda === */
.contacts-search {
  align-self: flex-start;          
  width: min(250px, 100%);            
}

.filter-group.filter-input {
  flex: 1;
  width: 100%;
  height: 42px;                       
  background-color: #f8f9fa;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.04);
  transition: box-shadow .2s ease, border-color .2s ease, background-color .2s ease;
  position: relative;                 
}

.filter-group.filter-input:focus-within {
  border-color: #94a3b8;              
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  background-color: #ffffff;
}

.filter-group.filter-input input {
  width: 100%;
  height: 100%;
  padding: 0.65rem 1rem 0.65rem 2.4rem;
  border: 0;
  background: transparent;
  color: #0f172a;
  font-size: 0.95rem;
  font-weight: 500;
  outline: none;
}

.filter-group.filter-input input::placeholder {
  color: #94a3b8;                     
  font-weight: 500;
}

.filter-group.filter-input input:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.filter-group.filter-input .select-icon {
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.05rem;
  color: #64748b;
  opacity: .9;
}

@media (max-width: 640px) {
  .contacts-search {
    width: 100%;
  }
}

/* === Selector === */
.filter-group {
  position: relative;
  display: flex;
  align-items: center;
  width: 150px;
  height: 42px;
  background-color: #f8f9fa;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.select-icon {
  position: absolute;
  left: 12px;
  font-size: 1.1rem;
  color: #505050;
  pointer-events: none;
}

.filter-group select {
  width: 100%;
  padding: 0.65rem 1rem 0.65rem 2.4rem;
  border: none;
  border-radius: 0.75rem;
  background-color: transparent;
  color: #1e293b;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  appearance: none;
  transition: all 0.2s ease;
}

.filter-group select:focus {
  outline: none;
}

/* == Boton == */
.chip-toggle {
  display: inline-flex;
  align-items: center;
  gap: .5rem;
  height: 42px; 
  padding: 0 .9rem 0 2.4rem; 
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  background-color: #f8f9fa;
  color: #1e293b;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  position: relative;
  box-shadow: 0 2px 4px rgba(0,0,0,0.04);
  transition: all 0.2s ease;
}

.chip-toggle .chip-icon {
  position: absolute;
  left: 12px;
  font-size: 1.1rem;
  color: #505050;
  opacity: .9;
}

.chip-toggle.active {
  background-color: #2563eb;   
  border-color: #ffffff;     
  color: #ffffff;
}

.chip-toggle.active .chip-icon {
  color: #ffffff;
  opacity: 1;
}

.chip-toggle:disabled {
  opacity: .6;
  cursor: not-allowed;
}

/* ====== Table ======= */
.contacts-table {
  margin-top: 2rem;
}

/* ====== Paginacion ======= */
.contacts-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: .75rem;
  margin: 1rem 1rem 3rem;
  font-family: 'Manrope', sans-serif;
}

.contacts-pagination .pg-btn {
  padding: .45rem .85rem;
  font-size: .9rem;
  border: 1px solid #e2e8f0;
  border-radius: .6rem;
  background: #fff;
  color: #0f172a;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease, transform .08s ease;
}

.contacts-pagination .pg-btn:hover {
  background: #f8fafc;
}

.contacts-pagination .pg-btn:active {
  transform: translateY(1px);
}

.contacts-pagination .pg-btn:disabled {
  opacity: .5;
  cursor: not-allowed;
  transform: none;
}

.contacts-pagination .pg-info {
  font-size: .9rem;
  color: #475569;
}

.contacts-pagination .pg-info strong {
  color: #0f172a;
}

.contacts-pagination .pg-info small {
  color: #64748b;
  margin-left: .25rem;
  font-size: .85em;
}
</style>